import * as React from "react";
import { Dialog } from "radix-ui";
import { Button, cn, dialogBodyClass, dialogContentClass, dialogOverlayClass } from "@ozpanel/ui";

// SecretReveal — the one "copy this now" panel for every credential the server shows exactly once.
// Unifies the hand-rolled copies that had drifted apart: an app password (with the USERNAME, the
// half people get wrong, shown first), a mailbox password, a panel API key, an access token. One
// layout, one clipboard button, one "cannot be recovered" line the caller turns off for a token
// that is revocable rather than hashed.
export function SecretReveal({
  title,
  secret,
  username,
  usernameLabel = "Username for your mail app",
  note,
  extra,
  recoverable = false,
  onClose,
}: {
  title: string;
  secret: string;
  /** Shown ABOVE the secret, because an app password is used with localpart+tag@domain, not the
   *  mailbox address — a password shown alone is unusable and the failure looks like a wrong one. */
  username?: string;
  usernameLabel?: string;
  note?: React.ReactNode;
  /** A neutral extra line, e.g. "3 app passwords revoked, 2 connections closed." */
  extra?: React.ReactNode;
  /** When true, the credential is revocable and re-issuable rather than hashed — so the copy says
   *  "revoke it any time" instead of "cannot be recovered". */
  recoverable?: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked (insecure context) — the value is still selectable by hand */
    }
  };
  // A Radix dialog, not a hand-built overlay: focus is trapped inside it, and — the reason this was
  // changed — a tap OUTSIDE no longer closes it. The secret is shown exactly once; the old overlay
  // closed on a backdrop tap, so a stray touch on a phone threw away a credential nobody can
  // recover. Escape and outside taps are ignored; only "I have saved it" closes it.
  return (
    <Dialog.Root open onOpenChange={() => {}}>
      <Dialog.Portal>
        <Dialog.Overlay className={dialogOverlayClass} />
        <Dialog.Content
          className={dialogContentClass}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className={cn(dialogBodyClass, "p-5")}>
            <Dialog.Title className="text-[15px] font-medium">{title}</Dialog.Title>
            <Dialog.Description className="mt-2 text-[12.5px] leading-relaxed text-muted">
              {recoverable
                ? "Copy it now for wherever you are setting it up. You can revoke it at any time."
                : "Copy it now. It cannot be shown again, and nobody can recover it later."}
            </Dialog.Description>
            {username && (
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-wide text-muted">{usernameLabel}</div>
                <div className="mt-1 break-all rounded border border-line bg-bg2 px-3 py-2 font-mono text-[13px] select-all">{username}</div>
              </div>
            )}
            <div className="mt-3">
              {username && <div className="text-[11px] uppercase tracking-wide text-muted">Password</div>}
              <div className="mt-1 break-all rounded border border-line bg-bg2 px-3 py-2 font-mono text-[13.5px] select-all">{secret}</div>
            </div>
            {extra && <div className="mt-2 text-[12px] text-muted">{extra}</div>}
            {note && <div className="mt-2 text-[12px] leading-relaxed text-warn">{note}</div>}
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3.5">
            <Button variant="default" onClick={() => void copy()}>
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button variant="accent" onClick={onClose}>
              I have saved it
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
